import React from 'react';

import { useSafeIsFocused, useSafeRouteParams } from '@sg/garnish';

import {
  MenuContent,
  useMenuContent,
  useMenuContentData,
  useProductDetailsContentData,
} from '../../components';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuScreen = () => {
  //
  // ─── Navigation ──────────────────────────────────────────────────────

  const { restaurantSlug } = useSafeRouteParams<{ restaurantSlug: string }>();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { restaurantData, isFetching, hasError, hasNoProducts } = useMenuContentData({
    restaurantSlug,
  });
  const { productDetailsContentData } = useProductDetailsContentData();
  const { mappedContent, isFetchingMenuContent } = useMenuContent();

  const { categoriesBadges } = mappedContent;

  // ─────────────────────────────────────────────────────────────────────

  const isScreenFocused = useSafeIsFocused();
  const shouldRenderErrorView = hasError || !restaurantData || hasNoProducts;

  // When the menu screen is rendered without being focused, there won't be any data available.
  const isWaitingForFocus = !isScreenFocused && shouldRenderErrorView;

  if (isFetching || isWaitingForFocus || isFetchingMenuContent) {
    return <MenuContent.Loading />;
  }

  if (shouldRenderErrorView) {
    return <MenuContent.Error />;
  }

  // ─────────────────────────────────────────────────────────────────────

  const orderChannel = restaurantData.isOutpost ? 'outpost' : 'pickup';
  const locationName = restaurantData.name;

  return (
    <MenuContent.Ready
      // NOTE: We use the restaurant ID as the key to force a re-render when
      //       the restaurant data changes to prevent having a stale data and components.
      key={restaurantData.id}
      restaurantData={restaurantData}
      productDetailsContentData={productDetailsContentData}
      categoriesBadges={categoriesBadges}
      orderChannel={orderChannel}
      locationName={locationName}
    />
  );
};
